import { useEffect, useState } from "react";
import { Languages, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export function I18nBadge({ i18n, testid = "email-i18n-badge" }) {
  if (!i18n) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid={testid}>
      <span className="inline-flex items-center gap-1 border border-[#111110] px-2 py-0.5 font-mono uppercase tracking-wider">
        <Languages size={10} /> Idioma: {i18n.language_name} ({i18n.language_code})
      </span>
      {i18n.fallback && (
        <span className="inline-flex items-center gap-1 border border-amber-400 bg-amber-50 text-amber-800 px-2 py-0.5" data-testid="email-i18n-fallback">
          <AlertTriangle size={10} /> Sin traducción en {i18n.requested_language} · se envía en español
        </span>
      )}
      {!i18n.fallback && i18n.outdated && (
        <span className="inline-flex items-center gap-1 border border-amber-400 bg-amber-50 text-amber-800 px-2 py-0.5" data-testid="email-i18n-outdated">
          <AlertTriangle size={10} /> Traducción desactualizada respecto al español
        </span>
      )}
    </div>
  );
}

export function LanguageSelect({ value, onChange, languages, customerLanguage, testid = "email-language-select" }) {
  if (!languages?.length) return null;
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Idioma</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="ds-card px-2 py-1 text-xs rounded-none focus:border-[#6E8B3D] outline-none bg-white"
      >
        {languages.map((l) => (
          <option key={l.code} value={l.code}>
            {l.name}{l.code === customerLanguage ? " · cliente" : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

export function useActiveLanguages() {
  const [languages, setLanguages] = useState([]);
  useEffect(() => {
    api.get("/languages/active").then((r) => setLanguages(r.data.languages || [])).catch(() => {});
  }, []);
  return languages;
}

/** Selector de idioma + vista previa real del email (idioma del cliente por defecto). */
export function EmailPreviewBlock({ templateKey, repairId, docId, customerId, language, onLanguage, defaultOpen = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    let alive = true;
    setError("");
    api.post("/templates/i18n/email-preview", {
      template_key: templateKey, repair_id: repairId, doc_id: docId, customer_id: customerId, language_code: language || null,
    })
      .then((r) => {
        if (!alive) return;
        setData(r.data);
        if (!language && r.data.customer_language) onLanguage?.(r.data.customer_language, true);
      })
      .catch((e) => alive && setError(formatApiError(e)));
    return () => { alive = false; };
  }, [templateKey, repairId, docId, customerId, language]); // eslint-disable-line

  if (error) return <div className="text-xs text-red-700 border border-red-300 bg-red-50 px-3 py-2" data-testid="email-preview-error">{error}</div>;
  if (!data) return <div className="text-xs text-[#525252]" data-testid="email-preview-loading">Preparando vista previa…</div>;

  return (
    <div className="space-y-2 border border-[#E5E5E0] bg-[#FAFAF8] p-3" data-testid="email-preview-block">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <LanguageSelect value={language || data.customer_language} onChange={(v) => onLanguage?.(v, false)} languages={data.languages} customerLanguage={data.customer_language} />
        <button type="button" onClick={() => setOpen(!open)} data-testid="email-preview-toggle"
          className="inline-flex items-center gap-1 text-[11px] text-[#525252] hover:text-[#111110]">
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {open ? "Ocultar vista previa" : "Ver vista previa"}
        </button>
      </div>
      <I18nBadge i18n={data.i18n} />
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">Asunto</div>
        <div className="px-3 py-1.5 border border-[#E5E5E0] bg-white text-xs" data-testid="email-preview-subject">{data.subject}</div>
      </div>
      {open && (
        <iframe title="Vista previa email" srcDoc={data.body_html} className="w-full h-72 bg-white border border-[#E5E5E0]" data-testid="email-preview-iframe" />
      )}
    </div>
  );
}
